/**
 * Column geometry of the DICOM tag table.
 *
 * The table is laid out with `table-layout: fixed`, which the row
 * virtualization requires: only a window of the rows is mounted, and an auto
 * layout would size the columns to whichever rows happen to be scrolled into
 * view. The consequence is that a column can no longer grow for the one row
 * that needs more of it — every row has to fit the width declared here, so the
 * nesting indent has to be budgeted against that width instead of being left
 * to grow with the tree.
 */

/**
 * Declared column widths, in CSS pixels. The Value column takes whatever is
 * left, down to {@link MIN_VALUE_COLUMN_WIDTH_PX}.
 */
export const TAG_COLUMN_WIDTH_PX = 240;
export const VR_COLUMN_WIDTH_PX = 50;
export const NAME_COLUMN_WIDTH_PX = 220;
export const MIN_VALUE_COLUMN_WIDTH_PX = 200;

/**
 * Width below which the table scrolls sideways rather than squeezing its
 * columns.
 *
 * A fixed layout given less room than its declared widths scales every column
 * down in proportion, which would put the indent budget below back over the
 * tag identifier — and, at the sizes the dialog is actually opened at, shrink
 * the Value column to nothing. Scrolling keeps each column at the width the
 * budget was computed for, whatever the window does.
 */
export const TABLE_MIN_WIDTH_PX =
    TAG_COLUMN_WIDTH_PX +
    VR_COLUMN_WIDTH_PX +
    NAME_COLUMN_WIDTH_PX +
    MIN_VALUE_COLUMN_WIDTH_PX;

/**
 * Type of the tag identifiers, VRs and values. {@link RESERVED_TAG_WIDTH_PX}
 * reserves the identifier's width at this size.
 */
export const monospaceSx = { fontFamily: 'monospace', fontSize: 12 } as const;

/** Indent added per level of sequence nesting, in CSS pixels. */
export const DEPTH_INDENT_PX = 16;

/**
 * Horizontal padding of a table cell — the theme's `spacing(1)`. Declared here
 * because the indent is added to it and the budget below has to subtract it:
 * the two must not drift apart.
 */
export const CELL_PADDING_X_PX = 8;

/**
 * The part of the Tag column that is not the indent's to spend:
 *
 * - `(GGGG,EEEE)` is 11 monospace characters. They are reserved at 8px each,
 *   comfortably above the ~6.5px the table's 12px Roboto Mono measures, so
 *   that a fallback monospace face still fits.
 * - A sequence row also carries the 22px expand toggle, 4px of which is pulled
 *   back over the cell's padding by its negative margin.
 * - The cell's own padding either side, {@link CELL_PADDING_X_PX}.
 */
const TAG_TEXT_WIDTH_PX = 11 * 8;
const TOGGLE_WIDTH_PX = 22 - 4;
export const RESERVED_TAG_WIDTH_PX =
    TAG_TEXT_WIDTH_PX + TOGGLE_WIDTH_PX + 2 * CELL_PADDING_X_PX;

/**
 * Deepest level that is still indented in full.
 *
 * DICOM nests sequences to no defined limit, and the indent is padding inside
 * the Tag column, so an indent that keeps growing with the tree eventually
 * pushes the tag identifier out of the column that names it: at that point the
 * row reads `(3006,00…` and can no longer be told from its siblings, which for
 * a structure set is every row that matters. Past this depth the indent stops
 * growing — nesting is still carried by the item separator rows and by the
 * rule inset under each nested row — so the identifier is always readable
 * whatever the dataset nests.
 *
 * The bound is derived rather than chosen so that changing a column width or
 * the indent step cannot silently start clipping;
 * `dicomTagLayout.test.ts` pins the depths a real structure set reaches.
 */
export const MAX_INDENT_DEPTH = Math.floor(
    (TAG_COLUMN_WIDTH_PX - RESERVED_TAG_WIDTH_PX) / DEPTH_INDENT_PX
);

/** Left indent of a row nested `depth` levels deep, in CSS pixels. */
export function indentForDepth(depth: number): number {
    return Math.min(Math.max(depth, 0), MAX_INDENT_DEPTH) * DEPTH_INDENT_PX;
}
